import { Box } from "@chakra-ui/react";

/**
 * Bar — horizontal progress bar with tone fills.
 * design ref: styles.css:738–753 (.bar / .bar__fill)
 *             styles.css:1303–1307 (.adoption__bar — 6px size variant)
 */

export type BarTone = "default" | "success" | "warning" | "error" | "azurite";

export interface BarProps {
  value: number;
  /** Denominator; defaults to 100 so `value` can be a percentage. */
  max?: number;
  tone?: BarTone;
  /** Track height. sm = 4px (default `.bar`), md = 6px (`.adoption__bar`). */
  size?: "sm" | "md";
}

/* design ref: styles.css:748–753 — fill tone backgrounds.
   NOTE: azurite maps to p.600 (not 500) per the CSS. */
const TONE_BG: Record<BarTone, string> = {
  default: "neutral.500",
  success: "success.500",
  warning: "warning.500",
  error: "error.500",
  azurite: "p.600",
};

const SIZE_HEIGHT: Record<"sm" | "md", string> = {
  sm: "4px",
  md: "6px",
};

const Bar = ({
  value,
  max = 100,
  tone = "default",
  size = "sm",
}: BarProps) => {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));

  return (
    <Box
      position="relative"
      w="100%"
      h={SIZE_HEIGHT[size]}
      bg="neutral.200"
      borderRadius="component.sm" /* 4px */
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        bottom={0}
        h="100%"
        w={`${pct}%`}
        bg={TONE_BG[tone]}
        borderRadius="component.sm"
        transition="width 240ms cubic-bezier(0.2, 0.6, 0.2, 1)"
      />
    </Box>
  );
};

export default Bar;
